import React, {useEffect, useState} from "react";
import {
    BooleanInput,
    CreateProps,
    FileField,
    FileInput,
    NumberInput,
    SelectInput,
    SimpleForm,
    TextInput,
    useNotify,
} from "react-admin";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {useForm, useFormState} from "react-final-form";

import {CustomCreate} from "../../components/CustomCreate";
import {ReferenceInputSubject} from "../../components/reference-inputs/ReferenceInputSubject";
import {clientNoJson, endpoint} from "../../dataProvider";
import {getAccessToken} from "../../authProvider";
import {BiTrash} from "react-icons/bi";
import {FormProps} from "../../shared/types";
import {CustomToolbar} from "../../components/CustomToolbar";

const BACKEND_FIELDS = [
    {id: "business_name", name: "Destinatario / ragione sociale"},
    {id: "document_number", name: "Numero documento"},
    {id: "document_date", name: "Data documento"},
    {id: "delivery_date", name: "Data consegna"},
    {id: "address", name: "Indirizzo"},
    {id: "postal_code", name: "CAP"},
    {id: "locality", name: "Località"},
    {id: "province", name: "Provincia"},
    {id: "packages_number", name: "Numero colli"},
    {id: "weight", name: "Peso"},
    {id: "volume", name: "Volume"},
    {id: "value", name: "Valore documento"},
    {id: "cachet", name: "Contrassegno"},
    {id: "first_delivery_reference", name: "Primo riferimento consegna"},
    {id: "second_delivery_reference", name: "Secondo riferimento consegna"},
    {id: "notes", name: "Note"},
    {id: "aspect", name: "Aspetto beni"},
    {id: "sub_client", name: "Sotto-cliente"},
];

const FILE_EXTENSIONS = [
    {id: "xls", name: "Excel (.xls)"},
    {id: "xlsx", name: "Excel (.xlsx)"},
    {id: "xlsb", name: "Excel binario (.xlsb)"},
    {id: "csv", name: "CSV (.csv)"},
    {id: "txt", name: "Testo (.txt)"},
];

const DATE_FORMATS = [
    {id: "", name: "Rilevamento automatico"},
    {id: "%d/%m/%Y", name: "31/12/2026"},
    {id: "%d-%m-%Y", name: "31-12-2026"},
    {id: "%d%m%Y", name: "31122026"},
    {id: "%d/%m/%y", name: "31/12/26"},
    {id: "%d-%m-%y", name: "31-12-26"},
    {id: "%Y/%m/%d", name: "2026/12/31"},
    {id: "%Y/%m/%d %H:%M:%S.%f", name: "2026/09/15 00:00:00.000000000"},
    {id: "%Y-%m-%d", name: "2026-12-31"},
    {id: "%Y%m%d", name: "20261231"},
];

const BACKEND_FIELD_LABELS: Record<string, string> = BACKEND_FIELDS.reduce(
    (labels, field) => ({...labels, [field.id]: field.name}),
    {}
);

const FIELD_ALIASES: Record<string, string[]> = {
    business_name: ["destinatario", "ragione sociale", "cliente", "denominazione", "intestatario"],
    document_number: ["numero documento", "numero ddt", "num ddt", "n ddt", "ddt", "documento"],
    document_date: ["data documento", "data ddt", "data doc", "data"],
    delivery_date: ["data consegna", "data delivery", "consegna"],
    address: ["indirizzo", "via", "recapito"],
    postal_code: ["cap", "codice postale", "postal code"],
    locality: ["localita", "citta", "comune", "locality"],
    province: ["provincia", "prov", "pr"],
    packages_number: ["numero colli", "colli", "pezzi", "packages"],
    weight: ["peso", "peso kg", "kg", "weight"],
    volume: ["volume", "metri cubi", "mc", "m3"],
    value: ["valore documento", "imponibile", "valore", "totale documento"],
    cachet: ["contrassegno"],
    first_delivery_reference: ["rif 1", "riferimento 1", "primo riferimento"],
    second_delivery_reference: ["rif 2", "riferimento 2", "secondo riferimento"],
    notes: ["note", "annotazioni", "osservazioni"],
    aspect: ["aspetto beni", "aspetto", "descrizione merce"],
    sub_client: ["sotto cliente", "sub cliente", "filiale"],
};

const normalize = (value: string) => value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const similarity = (column: string, alias: string) => {
    const normalizedColumn = normalize(column);
    const normalizedAlias = normalize(alias);
    if (normalizedColumn === normalizedAlias) return 1;
    if (normalizedAlias.length >= 3 && normalizedColumn.includes(normalizedAlias)) return 0.92;
    if (normalizedColumn.length >= 3 && normalizedAlias.includes(normalizedColumn)) return 0.86;

    const columnWords = new Set(normalizedColumn.split(" "));
    const aliasWords = new Set(normalizedAlias.split(" "));
    const commonWords = [...columnWords].filter((word) => aliasWords.has(word));
    return commonWords.length / Math.max(columnWords.size, aliasWords.size);
};

const suggestMappings = (
    columns: string[],
    currentMapping: Record<string, string>,
    currentBusinessNameColumn?: string
) => {
    const nextMapping = {...currentMapping};
    const usedFields = new Set(Object.keys(nextMapping));
    const usedColumns = new Set(Object.values(nextMapping));
    let businessNameColumn = currentBusinessNameColumn;
    if (businessNameColumn) usedColumns.add(businessNameColumn);

    const candidates = columns.flatMap((column) => BACKEND_FIELDS.flatMap((field) => {
        if (usedColumns.has(column) || usedFields.has(field.id)) return [];
        const score = Math.max(...(FIELD_ALIASES[field.id] || [field.name]).map((alias) => similarity(column, alias)));
        return score >= 0.65 ? [{column, field: field.id, score}] : [];
    })).sort((first, second) => second.score - first.score);

    candidates.forEach(({column, field}) => {
        if (usedColumns.has(column) || usedFields.has(field)) return;
        if (field === "business_name") businessNameColumn = column;
        else nextMapping[field] = column;
        usedColumns.add(column);
        usedFields.add(field);
    });

    return {mapping: nextMapping, businessNameColumn};
};

const ImportTestDialog = ({result, onClose}: { result: any; onClose: () => void }) => {
    if (!result) return null;

    return <Dialog open onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>Risultato test import</DialogTitle>
        <DialogContent>
            <Typography paragraph>
                {result.summary.valid} righe valide
                su {result.summary.total}. {result.summary.invalid > 0 && `${result.summary.invalid} righe con errori.`}
            </Typography>
            <TableContainer style={{maxHeight: "60vh"}}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Riga file</TableCell>
                            {result.columns.map((column: any) => (
                                <TableCell key={column.key}>
                                    {BACKEND_FIELD_LABELS[column.key] || column.key}
                                    <Typography component="span" color="textSecondary"> ({column.source})</Typography>
                                </TableCell>
                            ))}
                            <TableCell>Errori</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {result.rows.map((row: any) => (
                            <TableRow key={row.row}
                                      style={{backgroundColor: row.errors.length ? "#fff3f3" : undefined}}>
                                <TableCell>{row.row}</TableCell>
                                {result.columns.map((column: any) => (
                                    <TableCell key={column.key}>{row.values[column.key] ?? "—"}</TableCell>
                                ))}
                                <TableCell style={{color: row.errors.length ? "#c62828" : "#2e7d32"}}>
                                    {row.errors.length ? row.errors.join(" · ") : "Nessuno"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={onClose} color="primary">Chiudi</Button></DialogActions>
    </Dialog>;
};

const useStyles = makeStyles((theme) => ({
    content: {
        alignSelf: "stretch",
        width: "100%",
        padding: theme.spacing(1, 2, 10),
        boxSizing: "border-box",
        "& .MuiFormControl-root": {
            width: "100%",
        },
        "& .RaInput-root": {
            width: "100%",
        },
    },
    formGrid: {
        width: "100%",
        margin: 0,
    },
    fileInput: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}));

const ImportHeadersMapper = () => {
    const {values} = useFormState();
    const form = useForm();
    const notify = useNotify();
    const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
    const [manualColumns, setManualColumns] = useState<string[]>([]);
    const [manualColumn, setManualColumn] = useState("");
    const [loading, setLoading] = useState(false);
    const [testResult, setTestResult] = useState<any>(null);
    const columns = [...detectedColumns, ...manualColumns];

    useEffect(() => {
        if (!detectedColumns.length && !manualColumns.length && Array.isArray(values.required_columns)) {
            setDetectedColumns(values.required_columns);
        }
    }, [values.required_columns, detectedColumns.length, manualColumns.length]);

    const updateRequiredColumns = (nextDetectedColumns: string[], nextManualColumns: string[]) => {
        form.change("required_columns", [...nextDetectedColumns, ...nextManualColumns]);
    };

    const readHeaders = async () => {
        const file = values.sample_file && values.sample_file.rawFile;
        if (!file) {
            notify("Seleziona prima un file di esempio", "warning");
            return;
        }

        const data = new FormData();
        data.append("file", file);
        data.append("header_row", String(values.header_row_display || 1));
        data.append("separator", values.separator || ";");
        data.append("encoding", values.encoding || "utf-8-sig");

        setLoading(true);
        try {
            const response = await clientNoJson(`${endpoint}/import-functions/preview_headers/`, {
                method: "POST",
                data,
                headers: {
                    Authorization: `Bearer ${getAccessToken()}`,
                    "Content-Type": "multipart/form-data",
                },
            });
            const nextColumns = response.data.columns as string[];
            setDetectedColumns(nextColumns);
            updateRequiredColumns(nextColumns, manualColumns);
            form.change("file_extension", response.data.file_extension);
            const suggested = suggestMappings(nextColumns, values.mapping || {}, values.business_name_column);
            form.change("mapping", suggested.mapping);
            form.change("business_name_column", suggested.businessNameColumn);
        } catch (error) {
            notify("Impossibile leggere le intestazioni: verifica file, riga e separatore", "error");
        } finally {
            setLoading(false);
        }
    };

    const addManualColumn = () => {
        const column = manualColumn.trim();
        if (!column) return;
        if (columns.includes(column)) {
            notify("Esiste già una colonna con questo nome", "warning");
            return;
        }
        const nextManualColumns = [...manualColumns, column];
        setManualColumns(nextManualColumns);
        updateRequiredColumns(detectedColumns, nextManualColumns);
        setManualColumn("");
    };

    const testImport = async () => {
        const file = values.sample_file && values.sample_file.rawFile;
        if (!file) {
            notify("Seleziona un file di esempio da testare", "warning");
            return;
        }
        if (!values.business_name_column) {
            notify("Associa prima la colonna del destinatario", "warning");
            return;
        }

        const data = new FormData();
        data.append("file", file);
        data.append("mapping", JSON.stringify(values.mapping || {}));
        data.append("business_name_column", values.business_name_column);
        data.append("header_row", String(values.header_row_display || 1));
        data.append("separator", values.separator || ";");
        data.append("encoding", values.encoding || "utf-8-sig");
        data.append("date_format", values.date_format || "");

        setLoading(true);
        try {
            const response = await clientNoJson(`${endpoint}/import-functions/test_import/`, {
                method: "POST",
                data,
                headers: {
                    Authorization: `Bearer ${getAccessToken()}`,
                    "Content-Type": "multipart/form-data",
                },
            });
            setTestResult(response.data);
        } catch (error: any) {
            const data = error.response && error.response.data;
            const message = data ? Object.values(data).flat().join(" ") : "Impossibile eseguire il test import";
            notify(message, "error");
        } finally {
            setLoading(false);
        }
    };

    const removeManualColumn = (column: string) => {
        const nextManualColumns = manualColumns.filter((item) => item !== column);
        const nextMapping = {...(values.mapping || {})};
        Object.keys(nextMapping).forEach((key) => {
            if (nextMapping[key] === column) delete nextMapping[key];
        });
        if (values.business_name_column === column) form.change("business_name_column", undefined);
        form.change("mapping", nextMapping);
        setManualColumns(nextManualColumns);
        updateRequiredColumns(detectedColumns, nextManualColumns);
    };

    const mapping: Record<string, string> = values.mapping || {};
    const selectedField = (column: string) => {
        if (values.business_name_column === column) return "business_name";
        return Object.keys(mapping).find((field) => mapping[field] === column) || "";
    };

    const changeMapping = (column: string, field: string) => {
        const nextMapping = {...mapping};
        Object.keys(nextMapping).forEach((key) => {
            if (nextMapping[key] === column || key === field) delete nextMapping[key];
        });
        if (field === "business_name") {
            form.change("business_name_column", column);
        } else {
            if (values.business_name_column === column) form.change("business_name_column", undefined);
            if (field) nextMapping[field] = column;
        }
        form.change("mapping", nextMapping);
    };

    return (
        <div style={{marginTop: 24}}>
            <Typography variant="h6">Intestazioni e associazioni</Typography>
            <Typography color="textSecondary" paragraph>
                Carica un file campione: non viene salvato. Seleziona la riga che contiene le intestazioni e associa
                ogni colonna al relativo campo.
            </Typography>
            <Button variant="contained" color="primary" onClick={readHeaders} disabled={loading}>
                {loading ? "Lettura intestazioni…" : "Leggi intestazioni"}
            </Button>
            <Button variant="outlined" color="primary" onClick={testImport} disabled={loading} style={{marginLeft: 12}}>
                Esegui test
            </Button>

            <Grid container spacing={1} alignItems="center" style={{marginTop: 12}}>
                <Grid item xs={12} sm={8}>
                    <TextField
                        label="Aggiungi colonna manualmente"
                        value={manualColumn}
                        onChange={(event) => setManualColumn(event.target.value)}
                        onKeyPress={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                addManualColumn();
                            }
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Button variant="outlined" onClick={addManualColumn}>Aggiungi</Button>
                </Grid>
            </Grid>

            {columns.length > 0 && (
                <Grid container spacing={2} style={{marginTop: 16}}>
                    {columns.map((column) => {
                        const currentField = selectedField(column);
                        return (
                            <React.Fragment key={column}>
                                <Grid item xs={12} md={5}>
                                    <Typography component="div" style={{paddingTop: 16}}>
                                        {manualColumns.includes(column) && (
                                            <Button size="small" color="primary"
                                                    onClick={() => removeManualColumn(column)}>
                                                <BiTrash size={22}/>
                                            </Button>
                                        )}
                                        {column}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <FormControl variant="outlined" fullWidth>
                                        <InputLabel id={`mapping-${column}`}>Campo</InputLabel>
                                        <Select
                                            labelId={`mapping-${column}`}
                                            label="Campo"
                                            value={currentField}
                                            onChange={(event) => changeMapping(column, event.target.value as string)}
                                        >
                                            <MenuItem value=""><em>Non importare</em></MenuItem>
                                            {BACKEND_FIELDS.map((field) => (
                                                <MenuItem
                                                    key={field.id}
                                                    value={field.id}
                                                    disabled={!!mapping[field.id] && mapping[field.id] !== column}
                                                >
                                                    {field.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </React.Fragment>
                        );
                    })}
                </Grid>
            )}
            {columns.length > 0 && !values.business_name_column && (
                <Typography color="error" style={{marginTop: 12}}>
                    Associa una colonna al campo “Destinatario / ragione sociale” prima di salvare.
                </Typography>
            )}
            <ImportTestDialog result={testResult} onClose={() => setTestResult(null)}/>
        </div>
    );
};

const validateImportFunction = (values: any) => {
    const errors: Record<string, string> = {};
    if (!values.client_id) errors.client_id = "Seleziona il cliente";
    if (!values.file_extension) errors.file_extension = "Seleziona il formato del file";
    if (!values.business_name_column) errors.business_name_column = "Associa la colonna del destinatario";
    return errors;
};

export const ImportFunctionForm = (props: FormProps) => {
    const {record} = props;
    const classes = useStyles();
    const initialValues = {
        separator: ";",
        encoding: "utf-8-sig",
        is_active: true,
        sum_new_service: false,
        add_note_rows: false,
        mapping: {},
        ...record,
        header_row_display: typeof record?.header_row === "number" ? record.header_row + 1 : 1,
    };

    return <SimpleForm
        {...props}
        validate={validateImportFunction}
        initialValues={initialValues}
    >
        <div className={classes.content}>
            <Grid container spacing={3} className={classes.formGrid}>
                <Grid item xs={12}>
                    <ReferenceInputSubject source="client_id" label="Cliente" filter={{type: "client"}} required
                                           fullWidth/>
                </Grid>
                <Grid item xs={12} className={classes.fileInput}>
                    <FileInput
                        source="sample_file"
                        label="File di esempio"
                        accept=".xls,.xlsx,.xlsb,.csv,.txt"
                        placeholder={<p>Trascina qui un file oppure clicca per selezionarlo</p>}
                    >
                        <FileField source="src" title="title"/>
                    </FileInput>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <NumberInput variant="outlined" source="header_row_display" label="Riga intestazioni" min={1}
                                 fullWidth/>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SelectInput variant="outlined" source="file_extension" label="Formato file"
                                 choices={FILE_EXTENSIONS} fullWidth/>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextInput variant="outlined" source="separator" label="Separatore CSV/TXT" fullWidth/>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <TextInput variant="outlined" source="encoding" label="Codifica CSV/TXT" fullWidth/>
                </Grid>
                <Grid item xs={12} md={6}>
                    <SelectInput variant="outlined" source="date_format" label="Formato data" choices={DATE_FORMATS}
                                 fullWidth/>
                </Grid>
                <Grid item xs={12} md={6}>
                    <BooleanInput source="is_active" label="Configurazione attiva"/>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h6">Comportamento import</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                    <BooleanInput
                        source="sum_new_service"
                        label="Accorpa righe con stessa bolla"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <BooleanInput
                        source="add_note_rows"
                        label="Crea righe nota dagli aspetti beni"
                    />
                </Grid>
            </Grid>
            <ImportHeadersMapper/>
        </div>
    </SimpleForm>;
};

export const ImportFunctionCreate = (props: CreateProps) => {
    return <CustomCreate
        {...props}
        transform={(data: any) => {
            const {sample_file, header_row_display, ...importFunction} = data;
            return {
                ...importFunction,
                header_row: Math.max(0, Number(header_row_display || 1) - 1),
            };
        }}
    >
        <ImportFunctionForm/>
    </CustomCreate>
};
